import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Button } from '@abyss/web/ui/Button';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Link } from '@abyss/web/ui/Link';
import { LoadingSpinner } from '@abyss/web/ui/LoadingSpinner';
import { Text } from '@abyss/web/ui/Text';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../../common/AdobeTagging/adobeLinkTrackEvent';
import {
  Constants,
  LOB,
  SHOULD_GET_HG_DATA,
} from '../../../../../common/Constants';
import { ConstantsLagoon } from '../../../../../common/ConstantsLagoon';
import { phoneOnly } from '../../../../../common/ConstantsStyles';
import { ContentWrapper } from '../../../../../common/ContentWrapper';
import { ImageComponent } from '../../../../../common/ImageComponent';
import { MemberInfo } from '../../../../../common/Portal/PortalMemberInfo';
import { Breadcrumb } from '../../../../../common/PSXHeader/Breadcrumb';
import { capitalizeFirstLetter } from '../../../../../common/Utils/capitalizeFirstLetter';
import { useAdobePageTrackEvent } from '../../../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../../hooks/useLagoon';
import { useProviderDetails } from '../../../../../hooks/useProviderDetails';
import { useProviderResultsV2 } from '../../../../../hooks/useProviderSearch';
import {
  NetworkAffiliationDetails,
  ProviderDetails,
  ProviderLocation,
  ProviderType,
} from '../../../../../models/ProviderDetails';
import { useGuestAcquisitionStore } from '../../../../../store/useGuestAcquisitionStore';
import { GuestAcquisitionState } from '../../../../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import {
  getFormattedPhoneNumber,
  getPrimaryPhone,
} from '../../../../../utils/phone.utils';
import { getFormattedAddress } from '../../../../../utils/providerDetails.utils';
import {
  capitalizeFirstLetters,
  getNameAttrs,
  normalizeMedicalGroupName,
  parseProviderName,
} from '../../../../../utils/providerNames.utils';
import { removeLeadingZeroes } from '../../../../../utils/string.utils';
import { getIsOxfordAndUSP } from '../../../../../utils/user.utils';
import { filterNetworkAffiliations } from '../../SearchResults/searchUtils';
import {
  ChoosePCPLocationAvatar,
  PCPCardWrapper,
  PCPHeaderContainer,
} from './ChoosePCPLocation.styled';
import {
  StyledConfirmButton,
  StyledConfirmPCPAddressCardText,
  StyledConfirmPCPCardHeading,
  StyledConfirmPCPContainer,
  StyledConfirmPCPHeading,
  StyledConfirmPCPInfo,
  StyledConfirmPCPInfoHeading,
  StyledConfirmPCPNameCardText,
  StyledNewPCPCard,
  StyledPreviousPCPCard,
} from './ConfirmPCPLocation.styled';

type Props = {
  breadcrumbs: any;
  curProviderId: string;
  curProviderLocationId: string;
  curProviderType: ProviderType;
  currentMember?: MemberInfo;
  curPCPId?: string;
  curIsStaffModel?: boolean;
  curNetworkTypeCode?: string;
  handleBackButtonClick: () => void;
  handleCancelConfirm: () => void;
  handleFinishAndReturn: () => void;
  handleSaveLocation: () => void;
  newProvider: ProviderDetails;
  newProviderLocation: ProviderLocation;
  selectedNetworkAffiliation?: NetworkAffiliationDetails;
  targetUrl: string;
};

const getFullProviderName = (provider) => {
  const {
    primaryDegrees,
    professionalDesignation,
    providerName,
    providerType,
  } = provider;

  const nameAttrs = getNameAttrs(provider);
  const formattedProviderName = parseProviderName(
    providerName,
    providerType,
    professionalDesignation,
    nameAttrs
  );

  return primaryDegrees
    ? `${formattedProviderName}, ${primaryDegrees?.[0]}`
    : formattedProviderName;
};

export const ConfirmPCPLocation = ({
  breadcrumbs,
  curProviderId,
  curProviderLocationId,
  curProviderType,
  currentMember,
  curPCPId,
  curIsStaffModel,
  curNetworkTypeCode,
  handleBackButtonClick,
  handleCancelConfirm,
  handleFinishAndReturn,
  handleSaveLocation,
  newProvider,
  newProviderLocation,
  selectedNetworkAffiliation,
  targetUrl,
}: Props) => {
  const phoneScreen = useMediaQuery(phoneOnly);
  const { t } = useTranslation();
  const curImgRef = useRef<HTMLImageElement>(null);
  const newImgRef = useRef<HTMLImageElement>(null);

  const [isLoading, setIsLoading] = useState<boolean>(
    !!curProviderId || !!curIsStaffModel
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [
    includeProviderGroupName,
    enableNetworkAffiliationFlow,
    enableOxfordPcpChanges,
    enableAllStaffModelEni,
    removeTruncatedDataCard,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.INCLUDE_PROVIDER_GROUP_NAME,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NETWORK_AFFILIATION_FLOW,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_OXFORD_PCP_CHANGES,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALL_STAFF_MODEL_ENI,
    ConstantsLagoon.FEATURE_FLAGS.REMOVE_TRUNCATION_DATACARD,
  ]);

  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();

  const { isOXMember } = getIsOxfordAndUSP(currentMember, 'M', featureFlags);

  const confirmNewPCP: string = 'confirm new pcp';

  // Use provider details API when providerId is available (non-staff model)
  const { data } = useProviderDetails({
    providerId: curProviderId,
    providerType: capitalizeFirstLetter(curProviderType?.toLowerCase()),
    locationId: curProviderLocationId,
    shouldGetHGData: SHOULD_GET_HG_DATA,
  });

  const [providerSearchResult, getProviderSearchResult] = useProviderResultsV2(
    {}
  );

  // Call provider search for staff model members
  useEffect(() => {
    if (
      curIsStaffModel ||
      (enableOxfordPcpChanges && isOXMember) ||
      (enableAllStaffModelEni && currentMember?.isUspNiceMember)
    ) {
      getProviderSearchResult({
        pcpID: curPCPId,
        providerType: !curIsStaffModel ? curProviderType : '',
        shouldGetHGData: SHOULD_GET_HG_DATA,
        isStaffModel: curIsStaffModel,
        networkTypeCode: curNetworkTypeCode,
      });
    }
  }, [
    curIsStaffModel,
    enableOxfordPcpChanges,
    isOXMember,
    enableAllStaffModelEni,
  ]);

  const { isNavigatingFromJarvis } = useGuestAcquisitionStore(
    useShallow((state: GuestAcquisitionState) => ({
      isNavigatingFromJarvis: state.isNavigatingFromJarvis,
    }))
  );

  const curProviderData = data?.providerDetails || data?.facilityDetails;

  const { data: providerSearchData } = providerSearchResult || {};
  const providerFromSearch =
    providerSearchData?.providers?.[0] || providerSearchData?.medicalGroup?.[0];

  const curProvider =
    curIsStaffModel ||
    (enableOxfordPcpChanges && isOXMember) ||
    (enableAllStaffModelEni && currentMember?.isUspNiceMember)
      ? providerFromSearch
      : curProviderData?.providerDetails;

  const curProviderLocation =
    curIsStaffModel ||
    (enableOxfordPcpChanges && isOXMember) ||
    (enableAllStaffModelEni && currentMember?.isUspNiceMember)
      ? {
          address: providerFromSearch?.address,
          phones: providerFromSearch?.phones,
          providerGroupsAffiliations:
            providerFromSearch?.providerGroupsAffiliations,
          networks: providerFromSearch?.networkAffiliations,
        }
      : curProviderData?.providerDetails?.providerLocations?.[0];

  useEffect(() => {
    if (!curIsStaffModel && curProviderData) {
      setIsLoading(false);
    }
    if (curIsStaffModel && providerSearchData?.providers !== undefined) {
      setIsLoading(false);
    }
  }, [curProviderData, providerSearchData, curIsStaffModel]);

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: confirmNewPCP,
    sitesectionLevel1: 'pcp',
  });

  useEffect(() => {
    adobePageTrackEvent();
  }, []);

  if (isLoading) {
    return (
      <ContentWrapper>
        <LoadingSpinner
          ariaLoadingLabel="loading results"
          isLoading={isLoading}
          size="$md"
        />
      </ContentWrapper>
    );
  }

  const handleCloseConfirm = () => {
    adobeLinkTrackEvent({
      name: 'back',
      location: 'body:confirm pcp location',
      type: 'internal',
    });
    handleCancelConfirm();
  };

  const doSaveLocation = () => {
    if (isNavigatingFromJarvis) {
      handleFinishAndReturn();
    } else {
      setIsSaving(true);
      handleSaveLocation();
    }
  };

  const isChangingLocation =
    removeLeadingZeroes(curProviderId) ===
    removeLeadingZeroes(newProvider.providerId);
  const textSize = phoneScreen ? '$sm' : '$md';

  const findAffiliationByPcpId = (
    pcpId: string
  ): NetworkAffiliationDetails | null => {
    if (!providerSearchData) return null;

    const allProviders = [
      ...(providerSearchData.providers || []),
      ...(providerSearchData.medicalGroup || []),
    ];

    for (const prov of allProviders) {
      const found = prov?.networkAffiliations?.find(
        (affiliation) => affiliation.pcpId === pcpId
      );
      if (found) return found as NetworkAffiliationDetails;
    }
    return null;
  };

  const findAffiliationByNetworkId = (
    networkId: string
  ): NetworkAffiliationDetails | null => {
    if (!providerSearchData) return null;

    const allProviders = [
      ...(providerSearchData.providers || []),
      ...(providerSearchData.medicalGroup || []),
    ];

    for (const prov of allProviders) {
      const found = prov?.networkAffiliations?.find(
        (affiliation) => affiliation.networkAffiliationId === networkId
      );
      if (found) return found as NetworkAffiliationDetails;
    }
    return null;
  };

  const getMnrNetworkAffiliation = (
    locationNetworks
  ): NetworkAffiliationDetails | null => {
    if (!curPCPId) return null;
    if (curIsStaffModel) {
      return findAffiliationByPcpId(curPCPId);
    }
    return (
      filterNetworkAffiliations(locationNetworks).find(
        (affiliation) => affiliation.pcpId === curPCPId
      ) || null
    );
  };

  const getEniOxfordNetworkAffiliation =
    (): NetworkAffiliationDetails | null => {
      const isEniOrOxford =
        currentMember?.isUspNiceMember ||
        (enableOxfordPcpChanges && isOXMember);
      if (!isEniOrOxford || !curIsStaffModel || !curNetworkTypeCode)
        return null;
      return findAffiliationByNetworkId(curNetworkTypeCode);
    };

  const getNetworkAffiliationToShow = (
    isCurPCP: boolean,
    locationNetworks
  ): NetworkAffiliationDetails | null => {
    if (!enableNetworkAffiliationFlow) return null;

    if (isCurPCP) {
      if (currentMember?.lineOfBusiness === LOB.MNR && curPCPId) {
        return getMnrNetworkAffiliation(locationNetworks);
      }
      return getEniOxfordNetworkAffiliation();
    }

    const isEligibleForAffiliation =
      currentMember?.lineOfBusiness === LOB.MNR ||
      currentMember?.isUspNiceMember ||
      currentMember?.lineOfBusiness === LOB.IFP ||
      currentMember?.lineOfBusiness === LOB.CNS;
    if (isEligibleForAffiliation && selectedNetworkAffiliation) {
      return selectedNetworkAffiliation;
    }
    return null;
  };

  const PCPNameCard = ({ imgRef, provider, isStaffModel = false }) => {
    const { imageLocation: imgSrc, providerType, specialties } = provider;
    const fullProviderName = getFullProviderName(provider);

    const specialtyToShow = isStaffModel
      ? provider?.speciality || provider?.specialities?.[0]?.value
      : specialties?.[0];

    return (
      <StyledConfirmPCPInfo
        css={{
          width: isChangingLocation && !phoneScreen ? '407px' : '',
        }}
      >
        <Layout.Group alignItems={removeTruncatedDataCard ? 'top' : 'center'}>
          <ChoosePCPLocationAvatar>
            <ImageComponent
              imgRef={imgRef}
              imgSrc={imgSrc}
              providerType={providerType}
              sizeToken="$choosePCPLocationIconSize"
            />
          </ChoosePCPLocationAvatar>
          <Layout.Stack alignItems="left" space={0}>
            <Text fontWeight="bold" size={textSize}>
              <StyledConfirmPCPNameCardText
                cssProps={{ removeTruncatedDataCard }}
              >
                {fullProviderName}
              </StyledConfirmPCPNameCardText>
            </Text>
            <Text size={textSize}>
              <StyledConfirmPCPNameCardText
                cssProps={{ removeTruncatedDataCard }}
              >
                {specialtyToShow}
              </StyledConfirmPCPNameCardText>
            </Text>
          </Layout.Stack>
        </Layout.Group>
      </StyledConfirmPCPInfo>
    );
  };

  const PCPInfoCard = ({
    imgRef,
    isCurPCP = false,
    provider,
    location,
    isStaffModel = false,
  }) => {
    const { address, phones, providerGroupsAffiliations } = location;

    const formattedAddress = getFormattedAddress(
      address,
      includeProviderGroupName
    );
    const primaryPhoneNumber = getPrimaryPhone(
      phones?.phone,
      phones?.appointment
    );

    const prevOrNew = isCurPCP ? t('Previous') : t('New');
    const pcpOrLocation = isChangingLocation ? t('location') : t('PCP');

    const networkAffiliationToShow = getNetworkAffiliationToShow(
      isCurPCP,
      location?.networks
    );

    return (
      <Layout.Stack alignItems="left" grow space="8">
        <Heading
          color={isCurPCP ? '$gray7' : '$primary1'}
          css={{ ...StyledConfirmPCPCardHeading(phoneScreen) }}
          offset={1}
        >{`${prevOrNew} ${pcpOrLocation}`}</Heading>
        {!isChangingLocation ? (
          <Layout.Stack alignItems="left" grow space="4">
            <StyledConfirmPCPInfoHeading offset={2}>
              {t('PCP')}
            </StyledConfirmPCPInfoHeading>
            {PCPNameCard({
              imgRef,
              provider,
              isStaffModel:
                isStaffModel || isCurPCP
                  ? enableOxfordPcpChanges && isOXMember
                  : false,
            })}
            <StyledConfirmPCPInfoHeading offset={2}>
              {t('PRIMARY_CARE_PROVIDER.PCP_LOCATION')}
            </StyledConfirmPCPInfoHeading>
          </Layout.Stack>
        ) : null}
        <StyledConfirmPCPInfo>
          <Layout.Stack alignItems="left" grow space="4">
            {/* Network Affiliation Section */}
            {networkAffiliationToShow && (
              <>
                <Text
                  data-testid="pcp-confirmation-network-affiliation-header"
                  fontWeight="$bold"
                  size={textSize}
                >
                  <StyledConfirmPCPAddressCardText>
                    {t('NETWORK_AFFILIATION.NETWORK_AFFILIATION')}
                  </StyledConfirmPCPAddressCardText>
                </Text>
                <Text
                  data-testid="pcp-confirmation-network-affiliation-name"
                  size={textSize}
                >
                  <StyledConfirmPCPAddressCardText>
                    {capitalizeFirstLetters(
                      networkAffiliationToShow?.networkAffiliationName
                    )}
                  </StyledConfirmPCPAddressCardText>
                </Text>
                <div style={{ height: '12px' }} />
              </>
            )}

            {/* Medical Group Section */}
            {(networkAffiliationToShow?.medicalgrpaffilIdName?.[0] ||
              (includeProviderGroupName &&
                providerGroupsAffiliations?.[0])) && (
              <Text
                data-testid="pcp-confirmation-provider-group"
                fontWeight="$bold"
                size={textSize}
              >
                <StyledConfirmPCPAddressCardText
                  cssProps={{ removeTruncatedDataCard }}
                >
                  {networkAffiliationToShow?.medicalgrpaffilIdName?.[0]
                    ? capitalizeFirstLetters(
                        normalizeMedicalGroupName(
                          networkAffiliationToShow.medicalgrpaffilIdName[0]
                        ).value
                      )
                    : capitalizeFirstLetters(
                        providerGroupsAffiliations[0]?.value
                      )}
                </StyledConfirmPCPAddressCardText>
              </Text>
            )}
            <Text size={textSize}>
              <StyledConfirmPCPAddressCardText
                cssProps={{ removeTruncatedDataCard }}
              >
                {formattedAddress}
              </StyledConfirmPCPAddressCardText>
            </Text>
            <Link
              before={<IconMaterial icon="phone" />}
              data-testid="pcp-confirmation-phone-number"
              href={`tel:+1-${primaryPhoneNumber}`}
              size={textSize}
            >
              {getFormattedPhoneNumber(primaryPhoneNumber)}
            </Link>
          </Layout.Stack>
        </StyledConfirmPCPInfo>
      </Layout.Stack>
    );
  };

  return (
    <React.Fragment>
      <PCPHeaderContainer
        data-auto-testid="confirm-pcp-location-container"
        data-testid="confirm-pcp-location-container"
      >
        <ContentWrapper>
          <PCPCardWrapper>
            <Breadcrumb
              breadcrumbs={breadcrumbs}
              onBackButtonClick={handleBackButtonClick}
            />
            <StyledConfirmPCPHeading display={'h3'}>
              {isNavigatingFromJarvis
                ? t('PRIMARY_CARE_PROVIDER.ASSIGN_PCP_MESSAGE')
                : isChangingLocation
                ? t('PRIMARY_CARE_PROVIDER.CONFIRM_NEW_PCP_LOCATION')
                : t('PRIMARY_CARE_PROVIDER.CONFIRM_NEW_PCP')}
            </StyledConfirmPCPHeading>
            {/** Since jarvis rely on stoing data in thier own local storage , we need to do form action else domain wont change */}
            <form action={targetUrl} id="jarvisForm" method="post">
              <input id="jarvisPostbackData" name="json" type="hidden" />
            </form>
          </PCPCardWrapper>
        </ContentWrapper>
      </PCPHeaderContainer>
      <ContentWrapper>
        <StyledConfirmPCPContainer
          alignItems="left"
          grow={phoneScreen}
          space={phoneScreen ? 16 : 32}
        >
          {isChangingLocation ? (
            <Layout.Stack
              alignItems="left"
              css={{ marginBottom: phoneScreen ? '4px' : '-4px' }}
              data-testid="confirm-pcp-location-change"
              grow
              space="8"
            >
              <StyledConfirmPCPInfoHeading offset={2}>
                {t('PCP')}
              </StyledConfirmPCPInfoHeading>
              {PCPNameCard({
                imgRef: curImgRef,
                provider: curProvider,
                isStaffModel: curIsStaffModel,
              })}
            </Layout.Stack>
          ) : null}
          <Flex>
            <StyledNewPCPCard data-testid="confirm-pcp-new-pcp">
              {PCPInfoCard({
                imgRef: newImgRef,
                provider: newProvider,
                location: newProviderLocation,
              })}
            </StyledNewPCPCard>
            {curProvider ? (
              <StyledPreviousPCPCard data-testid="confirm-pcp-previous-pcp">
                {PCPInfoCard({
                  isCurPCP: true,
                  imgRef: curImgRef,
                  provider: curProvider,
                  location:
                    curIsStaffModel ||
                    (enableOxfordPcpChanges && isOXMember) ||
                    (enableAllStaffModelEni && currentMember?.isUspNiceMember)
                      ? curProviderLocation
                      : curProvider.providerLocations?.[0],
                  isStaffModel: curIsStaffModel,
                })}
              </StyledPreviousPCPCard>
            ) : null}
          </Flex>
          <Layout.Group alignLayout={phoneScreen ? 'right' : 'left'} space="12">
            <Button
              data-testid="pcp-confirmation-back-button"
              onClick={handleCloseConfirm}
              variant="outline"
            >
              {t('BACK')}
            </Button>
            {isSaving ? (
              <LoadingSpinner
                ariaLoadingLabel="loading results"
                isLoading={isSaving}
                size="$sm"
              />
            ) : (
              <StyledConfirmButton
                after={<IconMaterial color="$white" icon="chevron_right" />}
                data-testid="pcp-confirmation-submit-button"
                onClick={doSaveLocation}
              >
                {t('Yes, confirm')}
              </StyledConfirmButton>
            )}
          </Layout.Group>
        </StyledConfirmPCPContainer>
      </ContentWrapper>
    </React.Fragment>
  );
};
